import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MatchHighlightsModal } from '../components/MatchHighlightsModal';
import { MatchSimModal } from '../components/MatchSimModal';
import { CompetitionAwards } from '../components/CompetitionAwards';
import { getPlayerStat, isCWCYear, nextCWCYear, getTeamOverall } from '../utils/gameData';
import { buildCWCTeams } from '../utils/realTeams';
import { quickSimulate, updatePlayerStats, simulateKnockoutMatch } from '../utils/simulationEngine';
import { ArrowLeft, Flag, Lock, Lightning, Play, Eye, SoccerBall, Handshake, ShieldCheck, Cards, Trophy } from '@phosphor-icons/react';

const LEAGUE_GAMES_PER_TEAM = 8;
const KO_TOP_DIRECT = 12;        // top 12 qualify directly
const PLAYIN_SLOTS = [13, 14, 15, 16, 17, 18, 19, 20]; // 8 teams play in for 4 spots

// Build league fixtures: each team plays 8 matches (random opponents, no repeats)
const buildLeagueFixtures = (teamIds) => {
  // Each team must play 8 times. Total matches = 32*8/2 = 128
  // Build a graph using round-robin-ish selection: iterate, for each team pick 8 random opponents it hasn't played yet
  const fixtures = [];
  const playedPairs = new Set();
  const count = Object.fromEntries(teamIds.map(id => [id, 0]));
  // Greedy - shuffle teams, each team add games until has 8
  // Handle possibly impossible with retries
  const tryBuild = () => {
    fixtures.length = 0;
    playedPairs.clear();
    teamIds.forEach(id => count[id] = 0);
    const sorted = [...teamIds].sort(() => Math.random() - 0.5);
    for (let i = 0; i < sorted.length; i++) {
      const a = sorted[i];
      while (count[a] < LEAGUE_GAMES_PER_TEAM) {
        // Find an opponent that hasn't played a, has < 8 games, and not equal a
        const candidates = sorted.filter(b => b !== a && count[b] < LEAGUE_GAMES_PER_TEAM && !playedPairs.has([a, b].sort().join(':')));
        if (candidates.length === 0) return false;
        const b = candidates[Math.floor(Math.random() * candidates.length)];
        // Randomize home/away
        const home = Math.random() > 0.5 ? a : b;
        const away = home === a ? b : a;
        fixtures.push({ id: `cwcl-${fixtures.length}`, homeId: home, awayId: away, homeScore: null, awayScore: null, played: false, events: null, matchRatings: null });
        playedPairs.add([a, b].sort().join(':'));
        count[a]++; count[b]++;
      }
    }
    return true;
  };
  // Try up to 50 times
  for (let attempt = 0; attempt < 50; attempt++) {
    if (tryBuild()) return [...fixtures];
  }
  return [...fixtures]; // return best effort
};

export const ClubWorldCupPage = ({ gameState, onUpdateGameState }) => {
  const navigate = useNavigate();
  const teams = gameState?.teams || [];
  const currentWeek = gameState?.currentWeek || 1;
  const seasonComplete = currentWeek > 38;
  const year = gameState?.year || 2026;
  const cwcTeams = gameState?.cwcTeams || [];
  const [cwcState, setCwcState] = useState(() => gameState?.cwc || null);
  const [highlightsMatch, setHighlightsMatch] = useState(null);
  const [simModalMatch, setSimModalMatch] = useState(null);

  const eligible = isCWCYear(year);
  const nextYear = nextCWCYear(year);

  const allTeams = useMemo(() => cwcTeams || [], [cwcTeams]);
  const getTeam = (id) => allTeams.find(t => t.id === id);

  const startCWC = () => {
    let teamsCWC = cwcTeams;
    if (!teamsCWC || teamsCWC.length === 0) {
      teamsCWC = buildCWCTeams();
    }
    const teamIds = teamsCWC.map(t => t.id);
    const fixtures = buildLeagueFixtures(teamIds);
    const leagueStats = Object.fromEntries(teamIds.map(id => [id, { played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0 }]));
    const newCwc = {
      phase: 'league',
      fixtures,
      leagueStats,
      playin: null,
      knockout: null
    };
    setCwcState(newCwc);
    onUpdateGameState({ ...gameState, cwc: newCwc, cwcTeams: teamsCWC });
  };

  const resetCWC = () => {
    if (!window.confirm('Reset Club World Cup? All progress will be lost.')) return;
    setCwcState(null);
    onUpdateGameState({ ...gameState, cwc: null });
  };

  const applyLeagueMatch = (cwc, fixtureIdx, home, away, result) => {
    const next = { ...cwc, fixtures: [...cwc.fixtures], leagueStats: { ...cwc.leagueStats } };
    next.fixtures = next.fixtures.map((f, i) => i === fixtureIdx
      ? { ...f, homeScore: result.homeScore, awayScore: result.awayScore, played: true, events: result.events, matchRatings: result.matchRatings }
      : f);
    const hs = { ...next.leagueStats[home.id] };
    const as_ = { ...next.leagueStats[away.id] };
    hs.played++; hs.gf += result.homeScore; hs.ga += result.awayScore;
    as_.played++; as_.gf += result.awayScore; as_.ga += result.homeScore;
    if (result.homeScore > result.awayScore) { hs.won++; hs.points += 3; as_.lost++; }
    else if (result.homeScore < result.awayScore) { as_.won++; as_.points += 3; hs.lost++; }
    else { hs.drawn++; hs.points++; as_.drawn++; as_.points++; }
    next.leagueStats[home.id] = hs;
    next.leagueStats[away.id] = as_;
    return next;
  };

  const persistUpdate = (newCwc, result, home, away) => {
    const ewflUpd = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    const cwcUpd = updatePlayerStats(cwcTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    setCwcState(newCwc);
    onUpdateGameState({ ...gameState, teams: ewflUpd, cwcTeams: cwcUpd, cwc: newCwc });
  };

  const simOneLeagueMatch = (fixtureIdx) => {
    const f = cwcState.fixtures[fixtureIdx];
    if (f.played) return;
    const home = getTeam(f.homeId);
    const away = getTeam(f.awayId);
    if (!home || !away) return;
    const result = quickSimulate(home, away);
    const newCwc = applyLeagueMatch(cwcState, fixtureIdx, home, away, result);
    persistUpdate(newCwc, result, home, away);
  };

  const simAllLeague = () => {
    let current = cwcState;
    let ewflUpd = teams;
    let cwcUpd = cwcTeams;
    current.fixtures.forEach((f, i) => {
      if (f.played) return;
      const home = getTeam(f.homeId);
      const away = getTeam(f.awayId);
      if (!home || !away) return;
      const result = quickSimulate(home, away);
      current = applyLeagueMatch(current, i, home, away, result);
      ewflUpd = updatePlayerStats(ewflUpd, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
      cwcUpd = updatePlayerStats(cwcUpd, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    });
    setCwcState(current);
    onUpdateGameState({ ...gameState, teams: ewflUpd, cwcTeams: cwcUpd, cwc: current });
  };

  const openFullSimLeague = (fixtureIdx) => {
    const f = cwcState.fixtures[fixtureIdx];
    if (f.played) return;
    const home = getTeam(f.homeId);
    const away = getTeam(f.awayId);
    if (!home || !away) return;
    setSimModalMatch({ home, away, onApply: (result) => {
      const newCwc = applyLeagueMatch(cwcState, fixtureIdx, home, away, result);
      persistUpdate(newCwc, result, home, away);
    }});
  };

  // League standings
  const leagueStandings = useMemo(() => {
    if (!cwcState?.leagueStats) return [];
    return allTeams.map(t => ({ team: t, stats: cwcState.leagueStats[t.id] || { played:0,won:0,drawn:0,lost:0,gf:0,ga:0,points:0 } }))
      .sort((a, b) => {
        if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
        const gdA = a.stats.gf - a.stats.ga; const gdB = b.stats.gf - b.stats.ga;
        if (gdB !== gdA) return gdB - gdA;
        return b.stats.gf - a.stats.gf;
      });
  }, [cwcState?.leagueStats, allTeams]);

  const allLeagueDone = cwcState?.fixtures?.every(f => f.played);

  const advanceToPlayin = () => {
    const standings = leagueStandings;
    const direct = standings.slice(0, KO_TOP_DIRECT).map(s => s.team.id); // 12 direct KO entrants
    const playinTeams = standings.slice(KO_TOP_DIRECT, KO_TOP_DIRECT + 8).map(s => s.team.id); // 8 play-in teams (13-20)
    // Play-in: 4 matches, winners get 4 remaining KO spots. Single-leg knockout pairing 13vs20, 14vs19, 15vs18, 16vs17
    const playinMatches = [];
    for (let i = 0; i < 4; i++) {
      playinMatches.push({
        id: `playin-${i}`,
        homeId: playinTeams[i],
        awayId: playinTeams[7 - i],
        homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null
      });
    }
    const newCwc = { ...cwcState, phase: 'playin', playin: { matches: playinMatches, directQualifiers: direct } };
    setCwcState(newCwc);
    onUpdateGameState({ ...gameState, cwc: newCwc });
  };

  const simPlayinOne = (idx) => {
    const m = cwcState.playin.matches[idx];
    if (m.played) return;
    const home = getTeam(m.homeId);
    const away = getTeam(m.awayId);
    if (!home || !away) return;
    const result = simulateKnockoutMatch(home, away, 0, 0);
    applyPlayinResult(idx, home, away, result);
  };

  const applyPlayinResult = (idx, home, away, result) => {
    const newPlayin = { ...cwcState.playin, matches: [...cwcState.playin.matches] };
    const winnerId = result.pens ? (result.pens.winner === 'home' ? home.id : away.id) : (result.homeScore > result.awayScore ? home.id : result.awayScore > result.homeScore ? away.id : home.id);
    newPlayin.matches[idx] = { ...newPlayin.matches[idx], homeScore: result.homeScore, awayScore: result.awayScore, played: true, winnerId, events: result.events, matchRatings: result.matchRatings, pens: result.pens || null };
    const newCwc = { ...cwcState, playin: newPlayin };
    if (newPlayin.matches.every(m => m.played)) {
      const additionalIds = newPlayin.matches.map(m => m.winnerId);
      const ko16 = [...newPlayin.directQualifiers, ...additionalIds].sort(() => Math.random() - 0.5);
      const r16 = [];
      for (let i = 0; i < 8; i++) {
        r16.push({ homeId: ko16[i * 2], awayId: ko16[i * 2 + 1], homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null });
      }
      newCwc.knockout = { r16, qf: [], sf: [], final: [] };
      newCwc.phase = 'r16';
    }
    const ewflUpd = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    const cwcUpd = updatePlayerStats(cwcTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    setCwcState(newCwc);
    onUpdateGameState({ ...gameState, teams: ewflUpd, cwcTeams: cwcUpd, cwc: newCwc });
  };

  const openFullSimPlayin = (idx) => {
    const m = cwcState.playin.matches[idx];
    if (m.played) return;
    const home = getTeam(m.homeId);
    const away = getTeam(m.awayId);
    if (!home || !away) return;
    setSimModalMatch({ home, away, knockout: true, onApply: (result) => applyPlayinResult(idx, home, away, result) });
  };

  const simKnockoutOne = (roundKey, idx) => {
    const m = cwcState.knockout[roundKey][idx];
    if (m.played) return;
    const home = getTeam(m.homeId);
    const away = getTeam(m.awayId);
    if (!home || !away) return;
    const result = simulateKnockoutMatch(home, away, 0, 0);
    applyKnockoutResult(roundKey, idx, home, away, result);
  };

  const applyKnockoutResult = (roundKey, idx, home, away, result) => {
    const newKO = { ...cwcState.knockout, [roundKey]: [...cwcState.knockout[roundKey]] };
    const winnerId = result.pens ? (result.pens.winner === 'home' ? home.id : away.id) : (result.homeScore > result.awayScore ? home.id : result.awayScore > result.homeScore ? away.id : home.id);
    newKO[roundKey][idx] = { ...newKO[roundKey][idx], homeScore: result.homeScore, awayScore: result.awayScore, played: true, winnerId, events: result.events, matchRatings: result.matchRatings, pens: result.pens || null };
    const newCwc = { ...cwcState, knockout: newKO };
    if (newKO[roundKey].every(m => m.played)) {
      const winners = newKO[roundKey].map(m => m.winnerId);
      const nextRound = roundKey === 'r16' ? 'qf' : roundKey === 'qf' ? 'sf' : roundKey === 'sf' ? 'final' : null;
      if (nextRound && winners.length >= 2) {
        const nextMatches = [];
        for (let i = 0; i < winners.length; i += 2) {
          nextMatches.push({ homeId: winners[i], awayId: winners[i + 1], homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null });
        }
        newKO[nextRound] = nextMatches;
        newCwc.phase = nextRound;
      } else if (roundKey === 'final') {
        newCwc.phase = 'complete';
        newCwc.champion = winnerId;
      }
    }
    const ewflUpd = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    const cwcUpd = updatePlayerStats(cwcTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'cwc');
    let trophyHistory = gameState.trophyHistory || [];
    if (newCwc.phase === 'complete' && newCwc.champion) {
      const champion = allTeams.find(t => t.id === newCwc.champion);
      const alreadyRecorded = trophyHistory.some(h => h.year === (gameState.year || 2026) && h.competition === 'cwc');
      if (!alreadyRecorded && champion) {
        trophyHistory = [...trophyHistory, { season: gameState.season, year: gameState.year || 2026, competition: 'cwc', competitionLabel: 'Club World Cup', winnerId: champion.id, winnerName: champion.name }];
      }
    }
    setCwcState(newCwc);
    onUpdateGameState({ ...gameState, teams: ewflUpd, cwcTeams: cwcUpd, cwc: newCwc, trophyHistory });
  };

  const openFullSimKnockout = (roundKey, idx) => {
    const m = cwcState.knockout[roundKey][idx];
    if (m.played) return;
    const home = getTeam(m.homeId);
    const away = getTeam(m.awayId);
    if (!home || !away) return;
    setSimModalMatch({ home, away, knockout: true, onApply: (result) => applyKnockoutResult(roundKey, idx, home, away, result) });
  };

  // Top 100 CWC stats
  const cwcStats = useMemo(() => {
    const players = [...teams, ...cwcTeams].flatMap(t => t.players.map(p => ({ ...p, teamName: t.name })));
    return {
      scorers: [...players].filter(p => getPlayerStat(p, 'goals', 'cwc') > 0).sort((a, b) => getPlayerStat(b, 'goals', 'cwc') - getPlayerStat(a, 'goals', 'cwc')).slice(0, 100),
      assists: [...players].filter(p => getPlayerStat(p, 'assists', 'cwc') > 0).sort((a, b) => getPlayerStat(b, 'assists', 'cwc') - getPlayerStat(a, 'assists', 'cwc')).slice(0, 100),
      cs: [...players].filter(p => getPlayerStat(p, 'cleanSheets', 'cwc') > 0).sort((a, b) => getPlayerStat(b, 'cleanSheets', 'cwc') - getPlayerStat(a, 'cleanSheets', 'cwc')).slice(0, 100),
      cards: [...players].filter(p => getPlayerStat(p, 'yellowCards', 'cwc') > 0).sort((a, b) => getPlayerStat(b, 'yellowCards', 'cwc') - getPlayerStat(a, 'yellowCards', 'cwc')).slice(0, 100),
    };
  }, [teams, cwcTeams]);

  // Fixture by round (group of 16 per "matchday")
  const MatchRow = ({ match, idx, onQuick, onFull, small = false }) => {
    const home = getTeam(match.homeId);
    const away = getTeam(match.awayId);
    if (!home || !away) return null;
    return (
      <div className="flex items-center gap-2 text-xs p-1.5 bg-[#0A0A0C] rounded-sm" data-testid={`cwc-match-${idx}`}>
        <span className="text-white flex-1 truncate text-right">{home.flag} {home.name}</span>
        {match.played ? <span className="font-mono font-bold text-white px-2">{match.homeScore}-{match.awayScore}</span> : <span className="text-[#6E6E73] px-2">vs</span>}
        <span className="text-white flex-1 truncate">{away.flag} {away.name}</span>
        {!match.played ? (
          <div className="flex gap-1">
            <Button size="sm" onClick={onQuick} className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white h-6 text-[10px] px-2">Quick</Button>
            <Button size="sm" onClick={onFull} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white h-6 text-[10px] px-2">Full</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setHighlightsMatch({ match: { ...match, homeTeamName: home.name, awayTeamName: away.name }, homeTeam: home, awayTeam: away })} className="h-6 text-[10px] px-2 bg-transparent border-[#2C2C35] text-white">
            <Eye size={10} className="mr-1" /> Stats
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="cwc-back-btn"><ArrowLeft size={20} /></Button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <Flag size={32} className="text-[#007AFF]" weight="fill" /> FIFA Club World Cup
            </h1>
            <p className="text-[#A0A0A5] mt-1">32 teams • League (8 games each) • Top 12 direct KO + 4 via play-in • Every 4 years from 2028</p>
          </div>
          {cwcState && (
            <Button variant="outline" onClick={resetCWC} className="bg-transparent border-[#FF3B30] text-[#FF3B30]" data-testid="reset-cwc-btn">Reset</Button>
          )}
        </div>

        {!eligible && !cwcState ? (
          <div className="card-surface rounded-sm p-12 text-center">
            <Lock size={64} className="text-[#6E6E73] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">CWC Not Scheduled This Year</h2>
            <p className="text-[#A0A0A5] mb-4">The Club World Cup runs every 4 years starting 2028.</p>
            <p className="font-mono text-sm text-[#FF3B30] mt-4">Current Year: {year}</p>
            <p className="font-mono text-sm text-[#32D74B] mt-1">Next CWC: {nextYear}</p>
          </div>
        ) : eligible && !seasonComplete && !cwcState ? (
          <div className="card-surface rounded-sm p-12 text-center">
            <Lock size={64} className="text-[#6E6E73] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">Complete League First</h2>
            <p className="text-[#A0A0A5] mb-4">The Club World Cup plays after the league season ends.</p>
            <p className="font-mono text-sm text-[#FF3B30] mt-4">Current: Week {currentWeek}/38 • Year {year}</p>
          </div>
        ) : !cwcState ? (
          <div className="card-surface rounded-sm p-8 text-center">
            <Flag size={64} weight="fill" className="text-[#007AFF] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">FIFA Club World Cup {year}</h2>
            <p className="text-[#A0A0A5] mb-6">32 elite clubs from around the world. Ratings 86-97. Mbappé, Haaland, Vinicius, Bellingham, and more.</p>
            <Button data-testid="start-cwc-btn" onClick={startCWC} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white font-bold px-8">
              <Play size={18} className="mr-2" weight="fill" /> Start Tournament
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="main" className="w-full">
            <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-3 mb-6">
              <TabsTrigger value="main" className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white font-heading uppercase text-sm" data-testid="cwc-tab-main">Tournament</TabsTrigger>
              <TabsTrigger value="standings" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="cwc-tab-standings">League Table</TabsTrigger>
              <TabsTrigger value="awards" className="data-[state=active]:bg-[#FF9500] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="cwc-tab-awards">Awards</TabsTrigger>
            </TabsList>

            <TabsContent value="main">
              <div className="space-y-6">
                {/* League phase */}
                {cwcState.phase === 'league' && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="font-heading font-bold text-xl text-white uppercase">League Phase</h2>
                      <div className="flex gap-2">
                        {false && !allLeagueDone && (
                          null
                        )}
                        {allLeagueDone && (
                          <Button onClick={advanceToPlayin} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold" size="sm" data-testid="cwc-advance-playin">
                            <Trophy size={16} className="mr-1" /> Advance to Play-In
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#A0A0A5] mb-3">
                      {cwcState.fixtures.filter(f => f.played).length}/{cwcState.fixtures.length} games played
                    </p>
                    <ScrollArea className="h-[500px] card-surface rounded-sm p-2">
                      <div className="space-y-1">
                        {cwcState.fixtures.map((f, i) => (
                          <MatchRow key={f.id} match={f} idx={i}
                            onQuick={() => simOneLeagueMatch(i)}
                            onFull={() => openFullSimLeague(i)}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Play-in phase */}
                {cwcState.playin && (
                  <div>
                    <h2 className="font-heading font-bold text-xl text-white uppercase mb-3">Play-In Round (for 4 KO spots)</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {cwcState.playin.matches.map((m, i) => (
                        <div key={m.id} className="card-elevated rounded-sm p-3" data-testid={`cwc-playin-${i}`}>
                          <MatchRow match={m} idx={`playin-${i}`} onQuick={() => simPlayinOne(i)} onFull={() => openFullSimPlayin(i)} />
                          {m.winnerId && <p className="text-[10px] text-[#32D74B] mt-1">→ Qualified: {getTeam(m.winnerId)?.name}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Knockout */}
                {cwcState.knockout && ['r16', 'qf', 'sf', 'final'].map(roundKey => {
                  const round = cwcState.knockout[roundKey];
                  if (!round || round.length === 0) return null;
                  const roundLabel = roundKey === 'r16' ? 'Round of 16' : roundKey === 'qf' ? 'Quarter-Finals' : roundKey === 'sf' ? 'Semi-Finals' : 'FINAL';
                  const cols = roundKey === 'final' ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
                  return (
                    <div key={roundKey}>
                      <h2 className="font-heading font-bold text-xl text-white uppercase mb-3">{roundLabel}</h2>
                      <div className={`grid gap-3 ${cols}`}>
                        {round.map((m, i) => (
                          <div key={i} className="card-elevated rounded-sm p-3">
                            <MatchRow match={m} idx={`${roundKey}-${i}`}
                              onQuick={() => simKnockoutOne(roundKey, i)}
                              onFull={() => openFullSimKnockout(roundKey, i)} />
                            {m.winnerId && <p className="text-[10px] text-[#32D74B] mt-1">→ {getTeam(m.winnerId)?.name}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {cwcState.phase === 'complete' && cwcState.champion && (
                  <div className="card-surface rounded-sm p-8 text-center">
                    <Flag size={64} weight="fill" className="text-[#FFD700] mx-auto mb-3" />
                    <h2 className="font-heading font-black text-3xl text-[#FFD700] uppercase">Club World Cup Champions!</h2>
                    <p className="text-white text-2xl mt-2">{getTeam(cwcState.champion)?.flag} {getTeam(cwcState.champion)?.name}</p>
                    <p className="text-[#A0A0A5] mt-1">OVR: {getTeamOverall(getTeam(cwcState.champion))}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="standings">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3">
                  <h3 className="font-heading font-bold text-white uppercase tracking-wide">League Table (32 teams)</h3>
                </div>
                <ScrollArea className="h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-[#2C2C35] hover:bg-transparent sticky top-0 bg-[#0A0A0C] z-10">
                        <TableHead className="text-[#6E6E73] font-mono text-xs w-10">#</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs">Team</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">P</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">W</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">D</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">L</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-12">GD</TableHead>
                        <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-12">Pts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leagueStandings.map((row, idx) => {
                        const inDirect = idx < KO_TOP_DIRECT;
                        const inPlayin = idx >= KO_TOP_DIRECT && idx < KO_TOP_DIRECT + 8;
                        const borderColor = inDirect ? 'border-l-[#32D74B]' : inPlayin ? 'border-l-[#FFD700]' : 'border-l-transparent';
                        const gd = row.stats.gf - row.stats.ga;
                        return (
                          <TableRow key={row.team.id} className={`border-b border-[#2C2C35] border-l-2 ${borderColor} ${idx % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
                            <TableCell className="font-mono text-sm text-[#A0A0A5]">{idx + 1}</TableCell>
                            <TableCell className="text-sm text-white">{row.team.flag} {row.team.name}</TableCell>
                            <TableCell className="font-mono text-xs text-center text-[#A0A0A5]">{row.stats.played}</TableCell>
                            <TableCell className="font-mono text-xs text-center text-[#32D74B]">{row.stats.won}</TableCell>
                            <TableCell className="font-mono text-xs text-center text-[#A0A0A5]">{row.stats.drawn}</TableCell>
                            <TableCell className="font-mono text-xs text-center text-[#FF3B30]">{row.stats.lost}</TableCell>
                            <TableCell className={`font-mono text-xs text-center ${gd > 0 ? 'text-[#32D74B]' : gd < 0 ? 'text-[#FF3B30]' : 'text-[#A0A0A5]'}`}>{gd > 0 ? `+${gd}` : gd}</TableCell>
                            <TableCell className="font-mono text-xs text-center font-bold text-white">{row.stats.points}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
                <div className="p-3 border-t border-[#2C2C35] text-xs text-[#A0A0A5] flex gap-4">
                  <span><span className="inline-block w-3 h-3 bg-[#32D74B] mr-1 align-middle"></span> 1-12 Direct KO</span>
                  <span><span className="inline-block w-3 h-3 bg-[#FFD700] mr-1 align-middle"></span> 13-20 Play-In</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="awards">
              <CompetitionAwards teams={allTeams} competition="cwc" topN={25} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <MatchHighlightsModal
        isOpen={!!highlightsMatch}
        onClose={() => setHighlightsMatch(null)}
        match={highlightsMatch?.match}
        homeTeam={highlightsMatch?.homeTeam}
        awayTeam={highlightsMatch?.awayTeam}
        competitionLabel="Club World Cup"
      />

      <MatchSimModal
        isOpen={!!simModalMatch}
        onClose={() => setSimModalMatch(null)}
        homeTeam={simModalMatch?.home}
        awayTeam={simModalMatch?.away}
        competitionLabel="Club World Cup"
        knockout={simModalMatch?.knockout || false}
        onResult={(result) => { simModalMatch?.onApply?.(result); }}
      />
    </div>
  );
};

const CwcStatList = ({ label, data, statKey, color, icon: Icon }) => (
  <div className="card-surface rounded-sm">
    <div className="border-b border-[#2C2C35] px-4 py-3 flex items-center gap-2">
      <Icon size={18} className={color} weight="fill" />
      <h3 className="font-heading font-bold text-white uppercase tracking-wide">Top 100 {label}</h3>
    </div>
    <ScrollArea className="h-[500px]">
      {data.length === 0 ? (
        <p className="text-[#6E6E73] text-sm p-6 text-center">No Club World Cup stats yet.</p>
      ) : (
        <div className="p-2">
          {data.map((p, i) => (
            <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 ${i % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
              <span className={`font-mono text-sm font-bold w-6 ${i === 0 ? 'text-[#FFD700]' : 'text-[#A0A0A5]'}`}>{i + 1}</span>
              <span className="nationality-flag text-sm">{p.flag}</span>
              <span className="text-sm text-white flex-1 truncate">{p.name}</span>
              <span className="text-xs text-[#6E6E73] truncate max-w-[100px]">{p.teamName}</span>
              <span className={`font-mono text-sm font-bold ${color}`}>{getPlayerStat(p, statKey, 'cwc')}</span>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  </div>
);
